import { useCallback, useEffect, useRef, useState } from "react"
import DailyIframe, { type DailyCall, type DailyInputSettings, type DailyDeviceInfos } from "@daily-co/daily-js"
import { useDailyEvent } from "@daily-co/daily-react"
import type { CallLeaveReason, JoinInfo } from "../types"
import type { MainRoomResolveOptions } from "./useMainRoomTarget"
import type { PrejoinChoice } from "./PrejoinScreen"
import { enableDefaultNativeNoiseReduction, isNativeNoiseReductionPending, waitForNativeNoiseReduction } from "./nativeNoiseReduction"
import { cancelVideoEffectOperations, hasVideoEffectFailure, isVideoEffectUpdateActive, waitForVideoEffectUpdate } from "./videoEffects"
import { getDefaultMicrophoneId } from "./defaultMicrophone"

export type CallStage = "prejoin" | "joining" | "waiting" | "in-call" | "disconnected" | "left" | "error"
export type RoomTarget = { url: string; token?: string | null; label: string }

/**
 * Delay before an unmounted surface destroys its call object. React StrictMode (development)
 * mounts, unmounts, and remounts effects synchronously; destroying immediately would leave the
 * remounted surface holding a dead instance ("Use after destroy"). A remount within this window
 * cancels the pending destroy, while a real unmount (leaving the meeting page) still tears down.
 */
export const CALL_OBJECT_DESTROY_DELAY_MS = 250

const pendingDestroys = new WeakMap<DailyCall, number>()

export const cancelPendingDestroy = (callObject: DailyCall) => {
  const timer = pendingDestroys.get(callObject)
  if (timer !== undefined) {
    window.clearTimeout(timer)
    pendingDestroys.delete(callObject)
  }
}

/**
 * Daily allows one call instance per page. A surface that mounts while a previous one is still
 * inside its deferred-destroy window (navigating between meetings, "Try again") must tear that
 * instance down first, or Daily throws "Duplicate DailyIframe instances are not allowed" and a
 * call object could be reused while still joined to another room.
 */
export const destroyStaleCallInstance = async () => {
  let stale: DailyCall | undefined
  try {
    const getCallInstance = (DailyIframe as any)?.getCallInstance
    stale = typeof getCallInstance === "function" ? getCallInstance.call(DailyIframe) : undefined
  } catch {
    stale = undefined
  }
  if (!stale) return
  cancelPendingDestroy(stale)
  try {
    if (!stale.isDestroyed()) {
      await stale.destroy()
    }
  } catch {
    // The instance may already be gone.
  }
}

export const scheduleDestroy = (callObject: DailyCall) => {
  cancelPendingDestroy(callObject)
  const timer = window.setTimeout(() => {
    pendingDestroys.delete(callObject)
    try {
      if (!callObject.isDestroyed()) {
        void callObject.destroy()
      }
    } catch {
      // The call object may already be gone.
    }
  }, CALL_OBJECT_DESTROY_DELAY_MS)
  pendingDestroys.set(callObject, timer)
}

type UseDailyCallObjectLifecycleArgs = {
  callObject: DailyCall
  joinInfo: JoinInfo
  displayName: string
  onJoined: (localParticipantId: string) => void
  onWaiting: () => void
  onAccessGranted: (localParticipantId: string) => void
  onAccessDenied: () => void
  onError: (message: string) => void
  onLeft: (reason?: CallLeaveReason) => void
  resolveMainRoom?: (options?: MainRoomResolveOptions) => Promise<{ url: string; token?: string | null }>
}

/** The local mic/camera state as join options, so a room switch keeps both exactly as they were. */
const readLocalMediaState = (callObject: DailyCall): PrejoinChoice => {
  let audio = false
  let video = false
  try {
    audio = Boolean(callObject.localAudio())
    video = Boolean(callObject.localVideo())
  } catch {
    // A call object that is not joined yet reports nothing; treat both as off.
  }
  return { startAudioOff: !audio, startVideoOff: !video }
}

/** Re-apply the mic/camera state after a room join; Daily does not always honour the start flags on a re-join. */
const restoreLocalMediaState = (callObject: DailyCall, media: PrejoinChoice) => {
  try {
    callObject.setLocalAudio(!media.startAudioOff)
    callObject.setLocalVideo(!media.startVideoOff)
  } catch {
    // Nothing to restore if the call object is gone.
  }
}

const getLocalParticipantId = (callObject: DailyCall) => {
  try {
    const local: any = callObject.participants()?.local || {}
    const id = local.session_id || local.id || local.participant_id || ""
    return id ? String(id) : ""
  } catch {
    return ""
  }
}

const isLobbyAccess = (callObject: DailyCall) => {
  try {
    const access = callObject.accessState().access
    return access !== "unknown" && access.level === "lobby"
  } catch {
    return false
  }
}

/**
 * Drives one Daily call object through prejoin → join → (waiting room) → in-call → left,
 * mapping Daily events onto the renderer contract callbacks. Only this hook talks to the
 * call object's join/leave lifecycle; presentation components use Daily React hooks.
 */
export function useDailyCallObjectLifecycle({
  callObject,
  joinInfo,
  displayName,
  onJoined,
  onWaiting,
  onAccessGranted,
  onAccessDenied,
  onError,
  onLeft,
  resolveMainRoom,
}: UseDailyCallObjectLifecycleArgs) {
  const [stage, setStage] = useState<CallStage>("prejoin")
  const [requestingAccess, setRequestingAccess] = useState(false)
  const [reconnecting, setReconnecting] = useState(false)
  const [disconnectMessage, setDisconnectMessage] = useState<string | null>(null)
  const [mainRoomRecoveryVersion, setMainRoomRecoveryVersion] = useState(0)
  /** Label of the room the attendee is being moved to (breakout rooms), or null. */
  const [switchingTo, setSwitchingTo] = useState<string | null>(null)
  const switchingRef = useRef(false)
  const preparingTransferRef = useRef(false)
  const transferLeftRef = useRef(false)
  const transferErrorRef = useRef<string | null>(null)
  /** While in a breakout room: where to go if Daily drops this room (deleted or expired) instead of leaving the meeting. */
  const rejoinTargetRef = useRef<{ url: string; token?: string | null; label: string } | null>(null)
  const recoveringRef = useRef(false)
  const intentionalLeaveRef = useRef(false)
  const mountedRef = useRef(true)
  const operationEpochRef = useRef(0)
  const hasJoinedRef = useRef(false)
  const mediaRef = useRef<PrejoinChoice>({ startAudioOff: true, startVideoOff: true })
  const inputSettingsRef = useRef<DailyInputSettings | null>(null)
  const devicesRef = useRef<DailyDeviceInfos | null>(null)
  const settingsCaptureRef = useRef(0)
  const inputSettingsVersionRef = useRef(0)
  const devicesVersionRef = useRef(0)
  const initialMicrophoneRef = useRef<{ applied: boolean; pending?: Promise<string | undefined> }>({ applied: false })
  const resolveMainRoomRef = useRef(resolveMainRoom)
  resolveMainRoomRef.current = resolveMainRoom
  const stageRef = useRef<CallStage>("prejoin")
  const callbacksRef = useRef({ onJoined, onWaiting, onAccessGranted, onAccessDenied, onError, onLeft })
  callbacksRef.current = { onJoined, onWaiting, onAccessGranted, onAccessDenied, onError, onLeft }
  const joinInfoRef = useRef(joinInfo)
  joinInfoRef.current = joinInfo
  const displayNameRef = useRef(displayName)
  displayNameRef.current = displayName

  // Invalidate continuations before any later async work can rejoin or restart devices.
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      cancelVideoEffectOperations(callObject)
      operationEpochRef.current += 1
      settingsCaptureRef.current += 1
    }
  }, [callObject])

  const isCurrentOperation = useCallback((epoch: number) => (
    mountedRef.current && !intentionalLeaveRef.current && operationEpochRef.current === epoch
    && stageRef.current !== "left" && stageRef.current !== "error"
  ), [])

  const transition = useCallback((next: CallStage) => {
    if (!mountedRef.current) return
    stageRef.current = next
    setStage(next)
  }, [])

  const fail = useCallback((message: string) => {
    if (!mountedRef.current || intentionalLeaveRef.current || stageRef.current === "error" || stageRef.current === "left") return
    cancelVideoEffectOperations(callObject)
    if (hasJoinedRef.current) {
      setDisconnectMessage(message)
      setReconnecting(false)
      transition("disconnected")
      return
    }
    transition("error")
    callbacksRef.current.onError(message)
  }, [callObject, transition])

  const interruptTransferPreparation = useCallback((message: string) => {
    // No owned leave has started: a provider disconnect here is not part of the room move.
    // Cancel even if device capture or credential refresh never settles, and expose Rejoin.
    operationEpochRef.current += 1
    settingsCaptureRef.current += 1
    preparingTransferRef.current = false
    switchingRef.current = false
    recoveringRef.current = false
    setSwitchingTo(null)
    fail(message)
  }, [fail])

  const captureSettings = useCallback(async () => {
    const capture = ++settingsCaptureRef.current
    await waitForNativeNoiseReduction(callObject)
    await waitForVideoEffectUpdate(callObject)
    if (capture !== settingsCaptureRef.current || !mountedRef.current) return
    const inputVersion = inputSettingsVersionRef.current
    const devicesVersion = devicesVersionRef.current
    mediaRef.current = readLocalMediaState(callObject)
    const [input, devices] = await Promise.allSettled([
      Promise.resolve().then(() => callObject.getInputSettings?.()),
      Promise.resolve().then(() => callObject.getInputDevices?.()),
    ])
    if (capture !== settingsCaptureRef.current) return
    if (inputVersion === inputSettingsVersionRef.current && input.status === "fulfilled" && input.value) inputSettingsRef.current = input.value
    if (devicesVersion === devicesVersionRef.current && devices.status === "fulfilled" && devices.value) devicesRef.current = devices.value
  }, [callObject])

  const restoreSettings = useCallback(async (epoch: number) => {
    if (!isCurrentOperation(epoch)) return
    const devices = devicesRef.current
    // A missing/disconnected device or unsupported processor must not tear down a successful join.
    if (devices) {
      const audioDeviceId = "deviceId" in devices.mic ? devices.mic.deviceId : undefined
      const videoDeviceId = "deviceId" in devices.camera ? devices.camera.deviceId : undefined
      try { await callObject.setInputDevicesAsync({ audioDeviceId, videoDeviceId }) } catch { /* Device unavailable. */ }
      if (!isCurrentOperation(epoch)) return
      if ("deviceId" in devices.speaker) {
        try { await callObject.setOutputDeviceAsync({ outputDeviceId: devices.speaker.deviceId }) } catch { /* Unsupported output device. */ }
      }
    }
    if (!isCurrentOperation(epoch)) return
    if (inputSettingsRef.current) {
      try { await callObject.updateInputSettings(inputSettingsRef.current) } catch { /* Processor unavailable. */ }
    }
    if (isCurrentOperation(epoch)) restoreLocalMediaState(callObject, { ...mediaRef.current, startVideoOff: mediaRef.current.startVideoOff || hasVideoEffectFailure(callObject) })
  }, [callObject, isCurrentOperation])

  useDailyEvent("participant-updated", useCallback((event: any) => {
    if (event?.participant?.local && !switchingRef.current && stageRef.current === "in-call") {
      mediaRef.current = readLocalMediaState(callObject)
    }
  }, [callObject]))
  useDailyEvent("input-settings-updated", useCallback((event: any) => {
    if ((!switchingRef.current || isNativeNoiseReductionPending(callObject) || isVideoEffectUpdateActive(callObject)) && event?.inputSettings) {
      inputSettingsVersionRef.current += 1
      inputSettingsRef.current = event.inputSettings
    }
  }, [callObject]))
  useDailyEvent("selected-devices-updated", useCallback((event: any) => {
    if (!switchingRef.current && event?.devices) {
      devicesVersionRef.current += 1
      devicesRef.current = event.devices
    }
  }, []))

  const admit = useCallback(() => {
    if (!mountedRef.current || intentionalLeaveRef.current || stageRef.current !== "waiting") return
    transition("in-call")
    hasJoinedRef.current = true
    void captureSettings()
    setRequestingAccess(false)
    callbacksRef.current.onAccessGranted(getLocalParticipantId(callObject))
  }, [callObject, captureSettings, transition])

  const deny = useCallback(() => {
    if (!mountedRef.current || intentionalLeaveRef.current || stageRef.current !== "waiting") return
    cancelVideoEffectOperations(callObject)
    transition("left")
    setRequestingAccess(false)
    callbacksRef.current.onAccessDenied()
  }, [callObject, transition])

  const joinOptions = useCallback((extra: Partial<PrejoinChoice> = {}) => {
    const info = joinInfoRef.current
    const options: Record<string, unknown> = { url: info.url }
    if (typeof info.token === "string" && info.token) {
      options.token = info.token
    }
    const userName = displayNameRef.current || info.display_name || ""
    if (!options.token && userName) {
      options.userName = userName
    }
    return { ...options, ...extra, ...(hasVideoEffectFailure(callObject) ? { startVideoOff: true } : {}) }
  }, [callObject])

  const initialMicrophoneOptions = useCallback(async () => {
    const initial = initialMicrophoneRef.current
    const selectedMicrophone = devicesRef.current?.mic
    // A microphone picked while preAuth was pending must also win over startup defaults.
    if (initial.applied || (selectedMicrophone && "deviceId" in selectedMicrophone && selectedMicrophone.deviceId)) return {}
    const devicesVersion = devicesVersionRef.current
    initial.pending ||= getDefaultMicrophoneId(callObject)
    const audioSource = await initial.pending
    // A device choice made while enumeration was pending must win over startup defaults.
    if (initial.applied || devicesVersion !== devicesVersionRef.current) return {}
    return audioSource ? { audioSource } : {}
  }, [callObject])

  useEffect(() => {
    let cancelled = false
    const epoch = operationEpochRef.current
    // A remount (React StrictMode, hot reload) can hand us a call object that is already joined
    // or joining. Resume that state instead of re-running the prejoin setup, which Daily rejects.
    let currentState: string | null = null
    try {
      currentState = callObject.meetingState()
    } catch {
      currentState = null
    }
    if (currentState === "joined-meeting") {
      hasJoinedRef.current = true
      void captureSettings()
      transition(isLobbyAccess(callObject) ? "waiting" : "in-call")
      return
    }
    if (currentState === "joining-meeting") {
      transition("joining")
      return
    }
    const prepare = async () => {
      try {
        await callObject.preAuth(joinOptions() as any)
      } catch (err: any) {
        if (!cancelled && isCurrentOperation(epoch)) fail(err?.errorMsg || err?.message || "Unable to reach this meeting.")
        return
      }
      if (cancelled || !isCurrentOperation(epoch)) return
      const microphoneOptions = await initialMicrophoneOptions()
      if (cancelled || !isCurrentOperation(epoch)) return
      initialMicrophoneRef.current.applied = true
      try {
        await callObject.startCamera({ ...joinOptions({ startAudioOff: true, startVideoOff: true }), ...microphoneOptions } as any)
      } catch {
        // Device problems surface through useDevices(); the attendee can still join muted.
      }
      if (cancelled || !isCurrentOperation(epoch)) return
      // The microphone is still muted here, so noise reduction can be switched on before anyone speaks.
      await enableDefaultNativeNoiseReduction(callObject)
    }
    void prepare()
    return () => {
      cancelled = true
    }
  }, [callObject, captureSettings, fail, initialMicrophoneOptions, isCurrentOperation, joinOptions, transition])

  const join = useCallback(async (choice: PrejoinChoice) => {
    if (!mountedRef.current || intentionalLeaveRef.current || stageRef.current !== "prejoin") return
    const epoch = ++operationEpochRef.current
    mediaRef.current = choice
    transition("joining")
    try {
      // Joining immediately can cancel prejoin preparation; apply the same default once.
      const microphoneOptions = await initialMicrophoneOptions()
      if (!isCurrentOperation(epoch)) return
      initialMicrophoneRef.current.applied = true
      await callObject.join({ ...joinOptions(choice), ...microphoneOptions } as any)
    } catch (err: any) {
      if (isCurrentOperation(epoch)) fail(err?.errorMsg || err?.message || "Unable to start the video call.")
    }
  }, [callObject, fail, initialMicrophoneOptions, isCurrentOperation, joinOptions, transition])

  const requestAccess = useCallback(async () => {
    const epoch = operationEpochRef.current
    if (!isCurrentOperation(epoch)) return
    setRequestingAccess(true)
    try {
      const result = await callObject.requestAccess({ name: displayNameRef.current || joinInfoRef.current.display_name || "Guest" })
      if (!isCurrentOperation(epoch)) return
      if (result?.granted) {
        admit()
      } else {
        deny()
      }
    } catch {
      // access-state-updated / error events still resolve the waiting state.
    } finally {
      if (isCurrentOperation(epoch)) setRequestingAccess(false)
    }
  }, [admit, callObject, deny, isCurrentOperation])

  const finishRoomJoin = useCallback(async (epoch: number) => {
    await restoreSettings(epoch)
    if (!isCurrentOperation(epoch)) return false
    if (isLobbyAccess(callObject)) {
      transition("waiting")
      callbacksRef.current.onWaiting()
      void requestAccess()
    } else {
      transition("in-call")
    }
    return true
  }, [callObject, isCurrentOperation, requestAccess, restoreSettings, transition])

  useDailyEvent("joined-meeting", useCallback(() => {
    if (!mountedRef.current || intentionalLeaveRef.current || stageRef.current !== "joining") return
    if (isLobbyAccess(callObject)) {
      transition("waiting")
      callbacksRef.current.onWaiting()
      void requestAccess()
      return
    }
    transition("in-call")
    hasJoinedRef.current = true
    void captureSettings()
    callbacksRef.current.onJoined(getLocalParticipantId(callObject))
  }, [callObject, captureSettings, requestAccess, transition]))

  useDailyEvent("access-state-updated", useCallback((event: any) => {
    if (event?.access?.level === "full") {
      admit()
    }
  }, [admit]))

  const recoverToTarget = useCallback(async (requestedTarget?: RoomTarget) => {
    const target = requestedTarget || rejoinTargetRef.current
    if (!target || !mountedRef.current || recoveringRef.current || intentionalLeaveRef.current) return false
    cancelVideoEffectOperations(callObject)
    const epoch = ++operationEpochRef.current
    recoveringRef.current = true
    switchingRef.current = true
    preparingTransferRef.current = true
    transferLeftRef.current = false
    transferErrorRef.current = null
    setSwitchingTo(target.label)
    const userName = displayNameRef.current || joinInfoRef.current.display_name || ""
    try {
      await waitForNativeNoiseReduction(callObject)
      await waitForVideoEffectUpdate(callObject)
      if (!isCurrentOperation(epoch)) return false
      const fresh = resolveMainRoomRef.current ? await resolveMainRoomRef.current({ forceRefresh: true }) : target
      if (!isCurrentOperation(epoch)) return false
      const options: Record<string, unknown> = { url: fresh.url, token: fresh.token || "", ...mediaRef.current, ...(hasVideoEffectFailure(callObject) ? { startVideoOff: true } : {}) }
      if (userName) options.userName = userName
      preparingTransferRef.current = false
      try {
        await callObject.leave()
      } catch {
        // Already out of the room.
      }
      if (!isCurrentOperation(epoch)) return false
      await callObject.join(options as any)
      if (!await finishRoomJoin(epoch) || !isCurrentOperation(epoch)) return false
      rejoinTargetRef.current = null
      setDisconnectMessage(null)
      setReconnecting(false)
      setMainRoomRecoveryVersion((version) => version + 1)
      return true
    } catch (err: any) {
      if (isCurrentOperation(epoch)) fail(err?.errorMsg || err?.message || `Unable to return to ${target.label}.`)
      return false
    } finally {
      if (mountedRef.current && operationEpochRef.current === epoch) {
        recoveringRef.current = false
        switchingRef.current = false
        preparingTransferRef.current = false
        setSwitchingTo(null)
      }
    }
  }, [callObject, fail, finishRoomJoin, isCurrentOperation])

  const rejoin = useCallback(async () => {
    if (stageRef.current !== "disconnected") return
    await recoverToTarget({ url: joinInfoRef.current.url, token: joinInfoRef.current.token, label: "the main room" })
  }, [recoverToTarget])

  useDailyEvent("error", useCallback((event: any) => {
    const error = event?.error || event
    if (!mountedRef.current || intentionalLeaveRef.current) return
    if (error?.type === "ejected") {
      // A host removed this attendee; Daily follows up with left-meeting, which the stage guard ignores.
      if (stageRef.current === "left") return
      intentionalLeaveRef.current = true
      cancelVideoEffectOperations(callObject)
      operationEpochRef.current += 1
      settingsCaptureRef.current += 1
      rejoinTargetRef.current = null
      preparingTransferRef.current = false
      switchingRef.current = false
      recoveringRef.current = false
      setSwitchingTo(null)
      setRequestingAccess(false)
      transition("left")
      setReconnecting(false)
      callbacksRef.current.onLeft("removed")
      return
    }
    if (switchingRef.current) {
      const message = error?.msg || error?.message || event?.errorMsg || "You were disconnected while changing rooms."
      transferErrorRef.current = message
      if (preparingTransferRef.current) interruptTransferPreparation(message)
      return
    }
    if (error?.type === "not-allowed") {
      if (stageRef.current === "waiting") {
        deny()
      } else {
        fail(error?.msg || error?.message || event?.errorMsg || "Unable to join this meeting.")
      }
      return
    }
    if (rejoinTargetRef.current) {
      void recoverToTarget()
      return
    }
    fail(error?.msg || error?.message || event?.errorMsg || "The video call ran into a problem.")
  }, [callObject, deny, fail, interruptTransferPreparation, recoverToTarget, transition]))

  useDailyEvent("left-meeting", useCallback(() => {
    if (!mountedRef.current) return
    // Daily emits left-meeting right after a fatal error (for example "signaling connection
    // failed"); the shell is already showing the error screen with "Try again", so never turn
    // that into a silent navigation away from the meeting.
    if (stageRef.current === "left" || stageRef.current === "error" || stageRef.current === "disconnected") return
    // Leaving one room to join another (breakout rooms) is not leaving the meeting.
    if (switchingRef.current) {
      transferLeftRef.current = true
      if (preparingTransferRef.current) interruptTransferPreparation("You were disconnected while changing rooms.")
      return
    }
    if (rejoinTargetRef.current) {
      void recoverToTarget()
      return
    }
    if (hasJoinedRef.current && !intentionalLeaveRef.current) {
      fail("You were disconnected from the meeting.")
      return
    }
    cancelVideoEffectOperations(callObject)
    transition("left")
    setReconnecting(false)
    callbacksRef.current.onLeft()
  }, [callObject, fail, interruptTransferPreparation, recoverToTarget, transition]))

  useDailyEvent("network-connection", useCallback((event: any) => {
    if (!mountedRef.current || intentionalLeaveRef.current || event?.type !== "signaling") return
    if (event?.event === "interrupted") setReconnecting(true)
    if (event?.event === "connected") setReconnecting(false)
  }, []))

  const setRejoinTarget = useCallback((target: { url: string; token?: string | null; label: string } | null) => {
    rejoinTargetRef.current = target
  }, [])

  const leave = useCallback(async () => {
    const current = stageRef.current
    if (!mountedRef.current || current === "left") return
    const shouldLeaveCall = switchingRef.current || recoveringRef.current || !["prejoin", "error", "disconnected"].includes(current)
    // Notify once immediately; a pending room switch must not swallow the user's Leave.
    rejoinTargetRef.current = null
    intentionalLeaveRef.current = true
    cancelVideoEffectOperations(callObject)
    operationEpochRef.current += 1
    settingsCaptureRef.current += 1
    switchingRef.current = false
    preparingTransferRef.current = false
    recoveringRef.current = false
    setSwitchingTo(null)
    setRequestingAccess(false)
    setReconnecting(false)
    transition("left")
    callbacksRef.current.onLeft()
    if (shouldLeaveCall) {
      try { await callObject.leave() } catch { /* Already out of the call. */ }
    }
  }, [callObject, transition])

  /**
   * Move the attendee to another Daily room (a breakout room, or back to the main room) with
   * the same call object: leave, then join with the new URL and token. The stage stays
   * "in-call" so the shell's attendance and navigation hooks never fire; a failed join falls
   * back to the main room and, if that fails too, surfaces the shell error screen.
   */
  const switchRoom = useCallback(async ({ url, token, label }: { url: string; token?: string | null; label: string }) => {
    if (!mountedRef.current || intentionalLeaveRef.current || stageRef.current !== "in-call" || switchingRef.current) return false
    cancelVideoEffectOperations(callObject)
    const epoch = ++operationEpochRef.current
    switchingRef.current = true
    preparingTransferRef.current = true
    transferLeftRef.current = false
    transferErrorRef.current = null
    setSwitchingTo(label)
    const userName = displayNameRef.current || joinInfoRef.current.display_name || ""
    let leftPreviousRoom = false
    // Keep the attendee's mic and camera exactly as they were: Daily starts a fresh join with
    // whatever the room defaults are, which would silently turn cameras off on every move.
    try {
      await captureSettings()
      if (!isCurrentOperation(epoch)) return false
      const fresh = url === joinInfoRef.current.url && resolveMainRoomRef.current ? await resolveMainRoomRef.current() : { url, token }
      if (!isCurrentOperation(epoch)) return false
      const target: Record<string, unknown> = { url: fresh.url, token: fresh.token || "", ...mediaRef.current, ...(hasVideoEffectFailure(callObject) ? { startVideoOff: true } : {}) }
      if (userName) target.userName = userName
      preparingTransferRef.current = false
      await callObject.leave()
      leftPreviousRoom = true
      if (!isCurrentOperation(epoch)) return false
      await callObject.join(target as any)
      if (!await finishRoomJoin(epoch) || !isCurrentOperation(epoch)) return false
      return true
    } catch {
      // A refresh/preparation failure happens while the original room is still healthy.
      // Joining again here would fail and misleadingly label that live call disconnected.
      if (!isCurrentOperation(epoch)) return false
      if (!leftPreviousRoom) {
        let providerEnded = false
        try { providerEnded = ["left-meeting", "error"].includes(callObject.meetingState()) } catch { /* The recorded events remain authoritative. */ }
        if (transferLeftRef.current || transferErrorRef.current || providerEnded) {
          fail(transferErrorRef.current || "You were disconnected while changing rooms.")
        }
        return false
      }
      try {
        const fresh = resolveMainRoomRef.current ? await resolveMainRoomRef.current() : joinInfoRef.current
        if (!isCurrentOperation(epoch)) return false
        await callObject.join({ ...joinOptions(mediaRef.current), url: fresh.url, token: fresh.token || "" } as any)
        if (!await finishRoomJoin(epoch) || !isCurrentOperation(epoch)) return false
        rejoinTargetRef.current = null
        setMainRoomRecoveryVersion((version) => version + 1)
      } catch (err: any) {
        if (isCurrentOperation(epoch)) fail(err?.errorMsg || err?.message || `Unable to move you to ${label}.`)
      }
      return false
    } finally {
      if (mountedRef.current && operationEpochRef.current === epoch) {
        switchingRef.current = false
        preparingTransferRef.current = false
        setSwitchingTo(null)
      }
    }
  }, [callObject, captureSettings, fail, finishRoomJoin, isCurrentOperation, joinOptions])

  useEffect(() => {
    cancelPendingDestroy(callObject)
    return () => {
      scheduleDestroy(callObject)
    }
  }, [callObject])

  return { stage, requestingAccess, reconnecting, switchingTo, disconnectMessage, mainRoomRecoveryVersion, rejoin, join, leave, switchRoom, setRejoinTarget, getLocalParticipantId: () => getLocalParticipantId(callObject) }
}
